import { z } from "zod";

/** Which Square environment a set of credentials targets. */
export const squareEnvironmentSchema = z.enum(["production", "sandbox"]);
export type SquareEnvironment = z.infer<typeof squareEnvironmentSchema>;

/** Which Stripe mode a set of credentials targets. */
export const stripeModeSchema = z.enum(["test", "live"]);
export type StripeMode = z.infer<typeof stripeModeSchema>;

const secretValue = z.string().trim().min(1).max(512);

/**
 * Site-wide integration mode. One toggle drives both channels: when `devMode` is
 * on, every integration talks to its development environment (Square Sandbox +
 * Stripe Test); when off, to its production environment (Square Production +
 * Stripe Live). The matching credentials are stored per-environment so flipping
 * never requires re-entering keys.
 */
export const siteSettingsStatusSchema = z
  .object({
    devMode: z.boolean(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .strict();
export type SiteSettingsStatus = z.infer<typeof siteSettingsStatusSchema>;

export const updateSiteSettingsInputSchema = z
  .object({
    devMode: z.boolean(),
  })
  .strict();
export type UpdateSiteSettingsInput = z.infer<typeof updateSiteSettingsInputSchema>;

/** Whether one Square environment's credentials are present. */
export const squareEnvStatusSchema = z
  .object({
    configured: z.boolean(),
    hasAccessToken: z.boolean(),
    hasSignatureKey: z.boolean(),
    locationId: z.string().nullable(),
  })
  .strict();
export type SquareEnvStatus = z.infer<typeof squareEnvStatusSchema>;

/**
 * Public, non-secret status of the Square integration. Access tokens and
 * signature keys are **never** returned — only whether each environment's are
 * set. Which environment is live is driven by the site-wide dev-mode toggle.
 */
export const squareSettingsStatusSchema = z
  .object({
    notificationUrl: z.string().nullable(),
    production: squareEnvStatusSchema,
    sandbox: squareEnvStatusSchema,
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .strict();
export type SquareSettingsStatus = z.infer<typeof squareSettingsStatusSchema>;

/**
 * Admin-supplied Square credentials for one environment (chosen by `environment`).
 * Credential fields are optional so a save can rotate a subset; `notificationUrl`
 * is shared across environments (our single public webhook endpoint).
 */
export const updateSquareSettingsInputSchema = z
  .object({
    environment: squareEnvironmentSchema,
    accessToken: secretValue.optional(),
    webhookSignatureKey: secretValue.optional(),
    locationId: z.string().trim().min(1).max(64).optional(),
    notificationUrl: z.string().trim().url().max(2048).optional(),
  })
  .strict();
export type UpdateSquareSettingsInput = z.infer<typeof updateSquareSettingsInputSchema>;

/** Default Stripe Product metadata key matched against our SKU. */
export const DEFAULT_STRIPE_SKU_METADATA_KEY = "sku";

/** Whether one Stripe mode's credentials are present. */
export const stripeEnvStatusSchema = z
  .object({
    configured: z.boolean(),
    hasSecretKey: z.boolean(),
    hasWebhookSigningSecret: z.boolean(),
  })
  .strict();
export type StripeEnvStatus = z.infer<typeof stripeEnvStatusSchema>;

/**
 * Public, non-secret status of the Stripe integration. Secret keys and signing
 * secrets are **never** returned — only whether each mode's are set. Which mode
 * is live is driven by the site-wide dev-mode toggle.
 */
export const stripeSettingsStatusSchema = z
  .object({
    skuMetadataKey: z.string(),
    test: stripeEnvStatusSchema,
    live: stripeEnvStatusSchema,
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .strict();
export type StripeSettingsStatus = z.infer<typeof stripeSettingsStatusSchema>;

/**
 * Admin-supplied Stripe credentials for one mode (chosen by `mode`). Credential
 * fields are optional so a save can rotate a subset; `skuMetadataKey` is shared
 * across modes. An empty `skuMetadataKey` falls back to the default.
 */
export const updateStripeSettingsInputSchema = z
  .object({
    mode: stripeModeSchema,
    secretKey: secretValue.optional(),
    webhookSigningSecret: secretValue.optional(),
    skuMetadataKey: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type UpdateStripeSettingsInput = z.infer<typeof updateStripeSettingsInputSchema>;
